// Extracted from original/src/components/CurrencyVaultView.tsx (3,727 lines; ADR-08 §2.1 "copy, then cut logic"):
//   header summary        :2319-2378 (estimated total, sales profit, top currency, IQD vault)
//   currency cards        :2380-2445
//   entry form            :2450-3355 (title :2452-2464, approved rates card + inline edit :2466-2553, buy / sell pills
//                         :2555-2581, suspended panel :2583-2598, counterparty :2601-2633, customer block :2636-2933,
//                         info box :2935-2951, amount + bill counter + alerts :2953-3121, rate / cost / equivalent
//                         :3123-3180, live profit box :3182-3242, note :3246-3258, due date :3260-3335, submit :3337-3352)
//   movement ledger       :3357-3581
//   profit ranking        :3585-3664 (Super Manager only → `ranking` non-null for `view_currency_profits`, D-243)
// Kept: every class string, icon and size, the motion frames, the dropdown / inline-edit open flags (pure UI state),
// the `د.ع` / `$` unit markup and the emoji of the old labels.
// Cut: `localDataService.*`, `safeLocalStorage`, every balance write and the transaction list (:378-2120) → the gateway
// (`onSubmit`, D-232); `averageCurrencyCost` (:117-156) → the server pre-fill (D-065, D-216); `parseNumeric(...) *
// ...` arithmetic (equivalent :581-593, live profit :3183-3191, insufficient checks :595-605, :3002, :3023) → the
// server preview (`preview`, `stockShort`, `cashShort`; ADR-08 §2.1 item 2, ADR-02 §2.9); `handleSettlementCurrencyChange`
// conversions by the Dollar screen's rate field (:200-256, R-7) → `onSettlementChange` (D-067: the container re-fills
// from the server's USD pre-fill); `handlePrintTransactions` (:2224-2315, `window.open` + `document.write`) →
// `onPrint` (A4 template, D-146); `formatNumber(...)` / `toLocaleString` → preformatted labels; `currentUser.role`
// guards → `canEditRates` (`settings_currencies`, D-126) and `ranking` (`view_currency_profits`); `new Date()` due-date
// shortcuts → `onDueDateShortcut` (office clock, D-064); the bill-counter total (:549-564) → `onBillCountChange`.
// Changed by decision: holders of `currency_direct_deposit_withdraw` / `currency_opening_balance` get a small icon button
// in the form title row that switches the form to opening balance / direct deposit / direct withdrawal / write-off
// (D-068; the old deposit / withdraw paths existed without a control, I-1 — V-15); the settlement choice is offered on a
// sale in both modes and never on a buy (D-067 (1); old: only inside the customer block — F-243); the customer
// equivalent is the server's total, read-only (V-17); the ledger's delete button appears only when `onDelete` is given
// (corrections are D06 reversal entries, D-013 — V-16); the excess dialogs (R-16 / R-17) arrive with the shared D03
// surplus dialog. Added: data-testid attributes.
import { motion, AnimatePresence } from 'motion/react';
import {
  DollarSign, TrendingUp, TrendingDown, Plus, Minus, Search, Printer, Bell, User, Users, Shield, Coins, Clock, AlertCircle, AlertTriangle,
  Calculator, Ban, ListFilter, Edit2, Trash2, Save, X, ArrowLeftRight, ArrowDownUp,
} from 'lucide-react';
import { useState } from 'react';
import { NumericInput } from '../components/NumericInput.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { useT } from '../i18n-context.js';

export type CvOpType = 'buy' | 'sell' | 'direct';
export type CvDirectType = 'opening' | 'deposit' | 'withdraw' | 'writeoff';
export type CvCounterparty = 'box' | 'customer';
export type CvAllocation = 'debt' | 'trust';
export type CvSettlement = 'IQD' | 'USD';
export type CvRowKind = 'buy' | 'sell' | 'deposit' | 'withdraw' | 'opening' | 'writeoff' | 'transfer';
export type CvTypeFilter = 'all' | 'buy' | 'sell';

export interface CvHeader {
  readonly estimatedTotalLabel: string;
  /** With its sign: "+16,750", "-3,000" (old: a fixed "+" before any total, `:2350`, F-244). */
  readonly profitTotalLabel: string;
  readonly top: { readonly code: string; readonly profitLabel: string } | null;
  readonly mainVaultLabel: string;
}

export interface CvCurrencyCard {
  readonly code: string;
  readonly name: string;
  readonly symbol: string;
  readonly balanceLabel: string;
  readonly equivalentLabel: string;
  readonly suspended: boolean;
}

/** The selected currency as the form shows it (all labels preformatted by the container). */
export interface CvSelected {
  readonly code: string;
  readonly name: string;
  readonly symbol: string;
  readonly suspended: boolean;
  readonly buyRateLabel: string;
  readonly sellRateLabel: string;
  /** D-021: the units one quote refers to, formatted ("1", "1,000,000"). */
  readonly quoteUnitLabel: string;
  readonly multipleLabel: string;
  readonly bills: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly availableLabel: string;
}

export interface CvForm {
  readonly opType: CvOpType;
  readonly directType: CvDirectType;
  readonly counterparty: CvCounterparty;
  readonly customerName: string;
  readonly customerId: string;
  readonly newCustomerName: string;
  readonly allocation: CvAllocation;
  readonly settlement: CvSettlement;
  readonly receivedCash: string;
  readonly remainingOnAccount: string;
  readonly dueDate: string;
  readonly amount: string;
  readonly rate: string;
  readonly costRate: string;
  readonly note: string;
}

export interface CvCustomerOption {
  readonly id: string;
  readonly name: string;
  readonly amountLabel: string;
}

/** Server preview figures; null = nothing to show (old: amount and rate not both > 0). */
export interface CvPreview {
  readonly equivalentLabel: string;
  /** Unformatted decimal string of the same figure (the customer-equivalent field formats it itself). */
  readonly equivalentRaw: string;
  readonly profit?: { readonly label: string; readonly diffLabel: string; readonly absLabel: string; readonly tone: 'negative' | 'positive' | 'zero' };
}

export interface CvRow {
  readonly id: string;
  readonly kind: CvRowKind;
  readonly currencyName: string;
  readonly code: string;
  readonly symbol: string;
  readonly dateTimeLabel: string;
  readonly operator: string;
  readonly qtyLabel: string;
  /** Sale profit with sign, IQD; null = none. */
  readonly profit: { readonly label: string; readonly positive: boolean } | null;
  readonly costLabel: string | null;
  readonly equivalentLabel: string | null;
  readonly note: string | null;
}

export interface CvRankItem {
  readonly code: string;
  readonly name: string;
  readonly profitLabel: string;
  readonly sellCount: number;
  readonly pctShare: number;
  readonly isTop: boolean;
}

export interface CurrencyVaultProps {
  readonly header: CvHeader;
  readonly cards: readonly CvCurrencyCard[];
  readonly activeCode: string;
  readonly onSelectCurrency: (code: string) => void;
  readonly selected: CvSelected | null;

  readonly canEditRates: boolean;
  /** Inline rate edit (old :345-376) → `PUT /rates` with a PIN; resolves true when saved. */
  readonly onSaveRates: (buy: string, sell: string) => Promise<boolean>;
  readonly editRatesInitial: { readonly buy: string; readonly sell: string };

  readonly form: CvForm;
  readonly onFormChange: (patch: Partial<CvForm>) => void;
  readonly onOpTypeChange: (op: CvOpType) => void;
  readonly onCounterpartyChange: (cp: CvCounterparty) => void;
  readonly onSettlementChange: (s: CvSettlement) => void;
  readonly onAllocationChange: (a: CvAllocation) => void;
  /** D-067 (4) / D-066: false hides the USD option. */
  readonly usdAvailable: boolean;
  readonly iqdVaultLabel: string;
  readonly usdVaultLabel: string;
  /** Direct operations (D-068) the viewer may use; empty = no button. */
  readonly directTypes: readonly CvDirectType[];
  readonly customerOptions: readonly CvCustomerOption[];
  readonly onCustomerPick: (option: CvCustomerOption | 'new') => void;
  readonly onCashReceivedChange: (value: string) => void;
  readonly onRemainingChange: (value: string) => void;
  readonly onDueDateShortcut: (days: number) => void;
  readonly showBillCounter: boolean;
  readonly onToggleBillCounter: () => void;
  readonly billCounts: Readonly<Record<string, string>>;
  readonly onBillCountChange: (bill: string, count: string) => void;
  readonly preview: CvPreview | null;
  /** Sale / withdrawal above the stock (server verdict); `onFillMax` sets the whole balance (old :3036-3049). */
  readonly stockShort: { readonly requestedLabel: string; readonly availableLabel: string; readonly canFill: boolean } | null;
  readonly onFillMax: () => void;
  /** Box buy above the IQD on hand (server verdict). */
  readonly cashShort: { readonly costLabel: string; readonly availableLabel: string } | null;
  readonly canSubmit: boolean;
  readonly busy: boolean;
  readonly onSubmit: () => void;

  readonly rows: readonly CvRow[];
  readonly search: string;
  readonly onSearchChange: (v: string) => void;
  readonly startDate: string;
  readonly endDate: string;
  readonly onStartDateChange: (v: string) => void;
  readonly onEndDateChange: (v: string) => void;
  readonly currencyFilter: string;
  readonly currencyFilterOptions: ReadonlyArray<{ readonly code: string; readonly label: string }>;
  readonly onCurrencyFilterChange: (v: string) => void;
  readonly typeFilter: CvTypeFilter;
  readonly onTypeFilterChange: (v: CvTypeFilter) => void;
  readonly onPrint: () => void;
  readonly onDelete?: (id: string) => void;

  readonly ranking: readonly CvRankItem[] | null;
}

const BADGE: Readonly<Record<CvRowKind, string>> = {
  buy: 'bg-emerald-500/10 text-emerald-500',
  sell: 'bg-amber-500/10 text-amber-500',
  deposit: 'bg-indigo-500/10 text-indigo-500',
  opening: 'bg-indigo-500/10 text-indigo-500',
  withdraw: 'bg-rose-500/10 text-rose-500',
  writeoff: 'bg-rose-500/10 text-rose-500',
  transfer: 'bg-slate-100 text-slate-700',
};

export function CurrencyVault(props: CurrencyVaultProps) {
  const t = useT();
  const { header, selected, form } = props;
  const [isEditingRates, setIsEditingRates] = useState(false);
  const [editBuyRateStr, setEditBuyRateStr] = useState('');
  const [editSellRateStr, setEditSellRateStr] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const addNew = t('cv.add_new_account');
  const isDirect = form.opType === 'direct';
  const isSell = form.opType === 'sell';
  const isBuy = form.opType === 'buy';
  const usd = form.settlement === 'USD';
  const unit = usd ? '$' : t('cv.iqd');
  const unitEn = usd ? 'USD' : 'IQD';
  // the old fourth branch colours (deposit indigo, withdraw rose) serve the D-068 operations
  const submitTone = isBuy ? 'buy' : isSell ? 'sell' : form.directType === 'opening' || form.directType === 'deposit' ? 'deposit' : 'withdraw';
  const directNeedsCost = isDirect && (form.directType === 'opening' || form.directType === 'deposit');
  const stockLeaving = isSell || (isDirect && (form.directType === 'withdraw' || form.directType === 'writeoff'));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!props.busy && props.canSubmit) props.onSubmit();
  };
  const saveRates = async () => {
    if (await props.onSaveRates(editBuyRateStr, editSellRateStr)) setIsEditingRates(false);
  };

  const settlementButtons = (
    <div className="grid grid-cols-2 gap-2">
      {/* IQD Option */}
      <button
        type="button"
        data-testid="cv-settle-iqd"
        onClick={() => props.onSettlementChange('IQD')}
        className={`p-3 rounded-2xl border transition-all flex flex-col items-center justify-center gap-1 text-center cursor-pointer ${
          form.settlement === 'IQD'
            ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-700 dark:text-emerald-300 shadow-sm ring-2 ring-emerald-500/20 font-black'
            : 'bg-white dark:bg-[#05070a] border-gray-200 dark:border-[#30363d] text-slate-400 font-bold hover:border-slate-300'
        }`}
      >
        <span className="text-xl">🇮🇶</span>
        <div className="leading-tight">
          <span className="text-[10px] block">{t('cv.settle_iqd')}</span>
          <span className="text-[8px] opacity-70 block">
            {t('cv.vault_bal')}
            {props.iqdVaultLabel} {t('cv.iqd')}
          </span>
        </div>
      </button>

      {/* USD Option */}
      <button
        type="button"
        data-testid="cv-settle-usd"
        onClick={() => props.onSettlementChange('USD')}
        className={`p-3 rounded-2xl border transition-all flex flex-col items-center justify-center gap-1 text-center cursor-pointer ${
          form.settlement === 'USD'
            ? 'bg-amber-500/10 border-amber-500/50 text-amber-700 dark:text-amber-300 shadow-sm ring-2 ring-amber-500/20 font-black'
            : 'bg-white dark:bg-[#05070a] border-gray-200 dark:border-[#30363d] text-slate-400 font-bold hover:border-slate-300'
        }`}
      >
        <span className="text-xl">🇺🇸</span>
        <div className="leading-tight">
          <span className="text-[10px] block">{t('cv.settle_usd')}</span>
          <span className="text-[8px] opacity-70 block">
            {t('cv.vault_bal')}
            {props.usdVaultLabel} $
          </span>
        </div>
      </button>
    </div>
  );
  const showSettlement = isSell && props.usdAvailable;

  return (
    <div className="space-y-8" id="currencies_vault_container" data-testid="currencies-screen">
      {/* 1. Header Summary */}
      <div className="bg-gradient-to-br from-indigo-900 via-slate-900 to-indigo-950 dark:from-[#0d1117] dark:to-[#161b22] text-white p-8 rounded-[2.5rem] border border-indigo-950 dark:border-[#30363d] shadow-xl relative overflow-hidden">
        <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl"></div>
        <div className="absolute left-1/3 bottom-0 w-48 h-48 bg-slate-500/5 rounded-full blur-2xl"></div>

        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-300">{t('cv.header_title')}</span>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl md:text-5xl font-black font-sans tracking-tight" data-testid="cv-estimated-total">
                {header.estimatedTotalLabel}
              </span>
              <span className="text-lg font-bold text-indigo-200">{t('cv.iqd_long')}</span>
            </div>
            <p className="text-xs text-slate-400">{t('cv.header_note')}</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="bg-emerald-500/10 px-5 py-3 rounded-2xl border border-emerald-500/20">
              <span className="block text-[10px] font-bold text-emerald-300 uppercase tracking-widest">{t('cv.sales_profits')}</span>
              <span className="text-lg font-black font-sans text-emerald-400" data-testid="cv-profit-total">
                {header.profitTotalLabel} {t('cv.iqd')}
              </span>
            </div>

            {header.top && (
              <div className="bg-amber-500/10 px-5 py-3 rounded-2xl border border-amber-500/20">
                <span className="block text-[10px] font-bold text-amber-300 uppercase tracking-widest">{t('cv.top_currency')}</span>
                <span className="text-lg font-black font-sans text-amber-400 flex items-center gap-1.5">
                  <span>🏆 {header.top.code}</span>
                  <span className="text-xs text-amber-200/80 font-normal">
                    (+{header.top.profitLabel} د.ع)
                  </span>
                </span>
              </div>
            )}

            <div className="bg-white/5 dark:bg-white/10 px-5 py-3 rounded-2xl border border-white/10">
              <span className="block text-[10px] font-bold text-indigo-300 uppercase tracking-widest">{t('cv.main_vault')}</span>
              <span className="text-lg font-black font-sans">
                {header.mainVaultLabel} {t('cv.iqd')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Currencies Cards Grid */}
      <div>
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 dark:text-gray-400 mb-4 flex items-center gap-2">
          <DollarSign size={16} />
          {t('cv.cards_title')}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {props.cards.map((c) => {
            const isActive = props.activeCode === c.code;
            return (
              <motion.button
                key={c.code}
                data-testid={`cv-card-${c.code}`}
                onClick={() => props.onSelectCurrency(c.code)}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.98 }}
                className={`text-right w-full p-6 rounded-3xl border transition-all relative overflow-hidden ${
                  isActive
                    ? 'bg-indigo-600/10 border-indigo-500 dark:border-indigo-400 shadow-lg shadow-indigo-500/5'
                    : c.suspended
                      ? 'bg-rose-500/5 border-rose-200/50 dark:border-rose-950/20 hover:border-rose-400/50'
                      : 'bg-white dark:bg-[#0d1117] border-gray-150 dark:border-[#30363d] hover:border-indigo-400/50'
                }`}
              >
                {isActive && <div className="absolute top-3 left-3 w-3 h-3 bg-indigo-500 rounded-full animate-ping"></div>}

                {c.suspended && (
                  <div className="absolute top-3 left-3 px-2 py-0.5 bg-rose-100 dark:bg-rose-950/80 border border-rose-200 dark:border-rose-900 rounded-full text-rose-600 dark:text-rose-400 text-[8px] font-black tracking-wide leading-none select-none uppercase">
                    {t('cv.suspended_badge')}
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-2xl ${isActive ? 'bg-indigo-500 text-white' : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300'}`}>
                    <span className="font-mono text-xs font-black">{c.code}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-400 dark:text-gray-400">{c.name}</span>
                </div>

                <div className="space-y-1">
                  <div className="flex items-baseline gap-1.5 justify-end">
                    <span className="text-2xl font-black font-sans tracking-tight">{c.balanceLabel}</span>
                    <span className="text-xs font-bold text-slate-400 dark:text-gray-400">{c.symbol}</span>
                  </div>

                  <div className="text-xs text-slate-400 flex items-center justify-end gap-1 font-mono">
                    <span>{t('cv.equiv')}</span>
                    <span className="font-bold text-slate-600 dark:text-slate-300">{c.equivalentLabel} د.ع</span>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* 3. Operational Desk Section */}
      {selected && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* A. Transaction Entry Form */}
          <div className="lg:col-span-1 bg-white dark:bg-[#0d1117] p-6 rounded-[2.5rem] border border-gray-150 dark:border-[#30363d] shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-500/10 rounded-xl text-indigo-600 dark:text-indigo-400">
                <Calculator size={20} />
              </div>
              <div>
                <h4 className="font-black text-gray-900 dark:text-white">{t('cv.form_title', { name: selected.name })}</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('cv.form_sub')}</p>
              </div>
              {props.directTypes.length > 0 && (
                <button
                  type="button"
                  data-testid="cv-op-direct"
                  onClick={() => props.onOpTypeChange('direct')}
                  className={`mr-auto w-7 h-7 rounded-lg flex items-center justify-center font-bold transition-all shadow-sm cursor-pointer ${
                    isDirect ? 'bg-indigo-600 text-white' : 'bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                  }`}
                  title={t('cv.direct_button')}
                >
                  <ArrowDownUp size={12} />
                </button>
              )}
            </div>

            {/* Approved Exchange Rates Card */}
            {isEditingRates ? (
              <div className="bg-[#fcfdfe] dark:bg-[#0d1117] p-5 rounded-[2rem] border border-indigo-100 dark:border-indigo-950/40 shadow-sm relative overflow-hidden group space-y-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-1">
                    <ArrowLeftRight size={14} />
                    {t('cv.edit_rates')}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid="cv-rates-save"
                      onClick={() => void saveRates()}
                      className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-xl transition-all shadow-sm cursor-pointer"
                      title={t('cv.save_changes')}
                    >
                      <Save size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingRates(false)}
                      className="p-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-600 dark:text-rose-400 rounded-xl transition-all shadow-sm cursor-pointer"
                      title={t('cv.cancel')}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-emerald-500/5 p-3 rounded-2xl border border-emerald-500/10">
                    <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold mb-1">{t('cv.buy_rate')}</div>
                    <input
                      type="text"
                      data-testid="cv-rates-buy"
                      value={editBuyRateStr || ''}
                      onChange={(e) => setEditBuyRateStr(e.target.value)}
                      className="w-full text-center bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl py-1 font-mono font-black text-xs text-emerald-700 dark:text-emerald-300"
                    />
                  </div>
                  <div className="bg-blue-500/5 p-3 rounded-2xl border border-blue-500/10">
                    <div className="text-[10px] text-blue-600 dark:text-blue-400 font-bold mb-1">{t('cv.sell_rate')}</div>
                    <input
                      type="text"
                      data-testid="cv-rates-sell"
                      value={editSellRateStr || ''}
                      onChange={(e) => setEditSellRateStr(e.target.value)}
                      className="w-full text-center bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl py-1 font-mono font-black text-xs text-blue-700 dark:text-blue-300"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50/50 dark:bg-[#0d1117]/30 p-5 rounded-[2rem] border border-slate-150 dark:border-[#30363d] shadow-sm relative overflow-hidden group">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <ArrowLeftRight size={14} className="text-indigo-500" />
                    {t('cv.approved_rates')}
                  </span>

                  {props.canEditRates && (
                    <button
                      type="button"
                      data-testid="cv-rates-edit"
                      onClick={() => {
                        setEditBuyRateStr(props.editRatesInitial.buy);
                        setEditSellRateStr(props.editRatesInitial.sell);
                        setIsEditingRates(true);
                      }}
                      className="w-7 h-7 rounded-lg bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/40 dark:hover:bg-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold transition-all shadow-sm cursor-pointer"
                      title={t('cv.edit_rates')}
                    >
                      <Edit2 size={12} />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-center pt-1 border-t border-slate-100 dark:border-slate-800">
                  <div className="bg-emerald-500/10 p-3 rounded-2xl border border-emerald-500/15">
                    <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">{t('cv.buy_rate')}</div>
                    <div className="text-xs font-black text-emerald-700 dark:text-emerald-300 font-mono mt-0.5">{selected.buyRateLabel} د.ع</div>
                  </div>
                  <div className="bg-blue-500/10 p-3 rounded-2xl border border-blue-500/15">
                    <div className="text-[10px] text-blue-600 dark:text-blue-400 font-bold">{t('cv.sell_rate')}</div>
                    <div className="text-xs font-black text-blue-700 dark:text-blue-300 font-mono mt-0.5">{selected.sellRateLabel} د.ع</div>
                  </div>
                </div>
              </div>
            )}

            {/* Type Selector (Buy, Sell) */}
            <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-white/5 rounded-2xl border border-slate-200/50 dark:border-white/5">
              {(['buy', 'sell'] as const).map((op) => {
                const active = form.opType === op;
                return (
                  <button
                    key={op}
                    type="button"
                    data-testid={`cv-op-${op}`}
                    onClick={() => props.onOpTypeChange(op)}
                    className={`py-2 px-1 rounded-xl text-[10px] md:text-xs font-black transition-all ${
                      active ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {t(`cv.op_${op}`)}
                  </button>
                );
              })}
            </div>

            {selected.suspended ? (
              <div className="p-8 bg-rose-500/5 dark:bg-rose-950/5 border border-rose-200/50 dark:border-rose-900/30 rounded-3xl flex flex-col items-center justify-center text-center gap-4 py-12" dir="rtl">
                <div className="p-4 bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-full">
                  <Ban size={36} />
                </div>
                <div className="space-y-2">
                  <h5 className="font-black text-rose-800 dark:text-rose-400 text-sm">{t('cv.suspended_title')}</h5>
                  <p className="text-xs text-rose-600 dark:text-rose-400/80 leading-relaxed max-w-[240px] mx-auto">{t('cv.suspended_text', { name: selected.name })}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="cv-form">
                {isDirect ? (
                  /* D-068 operations (V-15): the counterparty selector's vocabulary */
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.direct_label')}</label>
                    <div className={`grid ${['grid-cols-1', 'grid-cols-1', 'grid-cols-2', 'grid-cols-3', 'grid-cols-4'][Math.min(props.directTypes.length, 4)]} p-1 bg-slate-100 dark:bg-white/5 rounded-2xl border border-slate-200/50 dark:border-white/5 gap-1`}>
                      {props.directTypes.map((d) => (
                        <button
                          key={d}
                          type="button"
                          data-testid={`cv-direct-${d}`}
                          onClick={() => props.onFormChange({ directType: d })}
                          className={`py-2 px-1 rounded-xl text-[10px] md:text-xs font-black transition-all cursor-pointer ${
                            form.directType === d ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                          }`}
                        >
                          {t(`cv.direct_${d}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Counterparty Selector: Direct Box vs Customer Account */
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.counterparty')}</label>
                    <div className="grid grid-cols-2 p-1 bg-slate-100 dark:bg-white/5 rounded-2xl border border-slate-200/50 dark:border-white/5">
                      <button
                        type="button"
                        data-testid="cv-cp-box"
                        onClick={() => props.onCounterpartyChange('box')}
                        className={`py-2 px-1 rounded-xl text-[10px] md:text-xs font-black transition-all cursor-pointer ${
                          form.counterparty === 'box' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        {t('cv.cp_box')}
                      </button>
                      <button
                        type="button"
                        data-testid="cv-cp-customer"
                        onClick={() => props.onCounterpartyChange('customer')}
                        className={`py-2 px-1 rounded-xl text-[10px] md:text-xs font-black transition-all cursor-pointer ${
                          form.counterparty === 'customer' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                        }`}
                      >
                        {t('cv.cp_customer')}
                      </button>
                    </div>
                  </div>
                )}

                {/* D-067: the settlement choice on a box sale (old: reachable only through the customer block, F-243) */}
                {!isDirect && form.counterparty === 'box' && showSettlement && (
                  <div className="space-y-2">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.settle_label')}</label>
                    {settlementButtons}
                  </div>
                )}

                {/* Customer Account Fields */}
                {!isDirect && form.counterparty === 'customer' && (
                  <div className="bg-amber-500/5 dark:bg-amber-500/10 p-4 rounded-2xl border border-amber-500/20 space-y-3.5 animate-in fade-in duration-200">
                    <div className="text-xs font-black text-amber-700 dark:text-amber-400 flex items-center gap-1.5 uppercase tracking-wide">
                      <Users size={15} />
                      <span>{t('cv.customer_details')}</span>
                    </div>

                    {/* Customer Selector Dropdown */}
                    <div className="relative">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{t('cv.customer_name')}</label>
                      <input
                        type="text"
                        data-testid="cv-customer"
                        value={form.customerName || ''}
                        onFocus={() => setShowCustomerDropdown(true)}
                        onChange={(e) => {
                          props.onFormChange({ customerName: e.target.value, customerId: '' });
                          setShowCustomerDropdown(true);
                        }}
                        placeholder={t('cv.customer_placeholder')}
                        className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-amber-500/20 rounded-xl px-3.5 py-2.5 font-bold text-xs"
                        autoComplete="off"
                      />

                      {showCustomerDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowCustomerDropdown(false)} />
                          <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-2xl shadow-xl z-50 max-h-[200px] overflow-y-auto p-1 space-y-1">
                            <button
                              type="button"
                              onClick={() => {
                                props.onCustomerPick('new');
                                setShowCustomerDropdown(false);
                              }}
                              className="w-full text-right px-3 py-2 text-xs font-black text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-xl transition-all flex items-center justify-between cursor-pointer"
                            >
                              <span>{addNew}</span>
                              <Plus size={14} />
                            </button>
                            {props.customerOptions
                              .filter((d) => !form.customerName || form.customerName === addNew || d.name.toLowerCase().includes(form.customerName.toLowerCase()))
                              .map((d) => (
                                <button
                                  key={d.id}
                                  type="button"
                                  onClick={() => {
                                    props.onCustomerPick(d);
                                    setShowCustomerDropdown(false);
                                  }}
                                  className="w-full text-right px-3 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#161b22] rounded-xl transition-all flex items-center justify-between cursor-pointer gap-2"
                                >
                                  <span className="truncate">{d.name}</span>
                                  <span className="font-mono text-[10px] text-slate-400 bg-slate-50 dark:bg-[#161b22] px-2 py-0.5 rounded-md border border-slate-100 dark:border-[#30363d] shrink-0">{d.amountLabel}</span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                    </div>

                    {form.customerName === addNew && (
                      <div className="space-y-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.new_account_name')}</label>
                        <input
                          type="text"
                          value={form.newCustomerName || ''}
                          onChange={(e) => props.onFormChange({ newCustomerName: e.target.value })}
                          placeholder={t('cv.new_account_placeholder')}
                          className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3.5 py-2.5 font-bold text-xs"
                        />
                      </div>
                    )}

                    {/* Allocation Type */}
                    <div className="space-y-1">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.account_type')}</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            props.onAllocationChange('debt');
                            setShowCustomerDropdown(true);
                          }}
                          className={`py-2 px-3 rounded-xl flex items-center justify-center gap-1.5 border text-xs transition-all cursor-pointer ${
                            form.allocation === 'debt'
                              ? 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-400 font-black'
                              : 'bg-white dark:bg-[#05070a] border-gray-200 dark:border-[#30363d] text-slate-400 font-bold'
                          }`}
                        >
                          <TrendingUp size={14} className={form.allocation === 'debt' ? 'text-rose-600 dark:text-rose-400' : 'text-rose-500'} />
                          <span>{t('cv.debts')}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            props.onAllocationChange('trust');
                            setShowCustomerDropdown(true);
                          }}
                          className={`py-2 px-3 rounded-xl flex items-center justify-center gap-1.5 border text-xs transition-all cursor-pointer ${
                            form.allocation === 'trust'
                              ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400 font-black'
                              : 'bg-white dark:bg-[#05070a] border-gray-200 dark:border-[#30363d] text-slate-400 font-bold'
                          }`}
                        >
                          <Shield size={14} className={form.allocation === 'trust' ? 'text-emerald-600 dark:text-emerald-400' : 'text-emerald-500'} />
                          <span>{t('cv.trusts')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Settlement Currency Selection (sale only, D-067) and the customer equivalent */}
                    <div className="space-y-2 pt-2 border-t border-amber-500/15">
                      {showSettlement && (
                        <>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.settle_choose')}</label>
                          {settlementButtons}
                        </>
                      )}

                      <div className="space-y-1.5 pt-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-[10px] font-black uppercase tracking-wider text-amber-900 dark:text-amber-300">{usd ? t('cv.cust_equiv_usd') : t('cv.cust_equiv_iqd')}</label>
                          <span className="text-[9px] font-extrabold text-amber-800 dark:text-amber-300 bg-amber-500/15 dark:bg-amber-500/20 px-2 py-0.5 rounded-md border border-amber-500/30">{t('cv.cust_equiv_badge')}</span>
                        </div>
                        <div className="relative">
                          <NumericInput
                            value={props.preview?.equivalentRaw ?? ''}
                            onChange={() => {}}
                            placeholder="0"
                            className="w-full bg-white dark:bg-[#05070a] border-2 border-amber-500/50 dark:border-amber-500/40 focus:border-amber-500 rounded-xl px-4 py-2.5 text-center font-mono font-black text-sm text-amber-900 dark:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500/20 shadow-xs"
                          />
                          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-black text-xs text-amber-600 dark:text-amber-400 pointer-events-none">{unit}</span>
                        </div>
                        <div className="flex items-center justify-between text-[9px] text-slate-400 px-1">
                          <span>{t(usd ? 'cv.cust_equiv_of_usd' : 'cv.cust_equiv_of_iqd', { amount: form.amount || '0', symbol: selected.symbol || selected.code })}</span>
                          {props.preview && (
                            <span className="font-mono font-bold text-amber-700 dark:text-amber-400">
                              {props.preview.equivalentLabel} {unit}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Payment Breakdown */}
                    <div className="space-y-2 pt-1 border-t border-amber-500/15">
                      <div className="text-[10px] font-black text-amber-800 dark:text-amber-300 flex items-center gap-1">
                        <Coins size={13} />
                        <span>{t('cv.breakdown')}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <div>
                          <label className="block text-[9px] font-bold text-slate-400 mb-1 uppercase">{t('cv.cash_received', { unit, unitEn })}</label>
                          <NumericInput
                            value={form.receivedCash ?? ''}
                            onChange={props.onCashReceivedChange}
                            placeholder="0"
                            className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2 text-center font-mono font-bold text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-bold text-slate-400 mb-1 uppercase">{t('cv.on_account', { unit, unitEn })}</label>
                          <NumericInput
                            value={form.remainingOnAccount ?? ''}
                            onChange={props.onRemainingChange}
                            placeholder="0"
                            className="w-full bg-white dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2 text-center font-mono font-bold text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Info Box */}
                <div className="p-4 bg-slate-50 dark:bg-white/5 rounded-2xl text-xs space-y-1.5 border border-gray-100 dark:border-[#30363d]">
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('cv.default_rate')}</span>
                    <span className="font-mono font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap" data-testid="cv-default-rate">
                      {selected.quoteUnitLabel} {selected.code} = {selected.buyRateLabel} د.ع
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">{t('cv.multiple_label')}</span>
                    <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{t('cv.multiple_value', { multiple: selected.multipleLabel })}</span>
                  </div>
                </div>

                {/* Amount Entry */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.amount_label', { symbol: selected.symbol })}</label>
                    {stockLeaving && (
                      <span className="text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400">
                        {t('cv.available')}
                        <span className="text-indigo-600 dark:text-indigo-400 font-black">
                          {selected.availableLabel} {selected.symbol}
                        </span>
                      </span>
                    )}
                    {isBuy && form.counterparty === 'box' && (
                      <span className="text-[10px] font-mono font-bold text-slate-500 dark:text-slate-400">
                        {t('cv.main_vault_balance')}
                        <span className="text-emerald-600 dark:text-emerald-400 font-black">
                          {props.iqdVaultLabel} د.ع
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      data-testid="cv-amount"
                      value={form.amount || ''}
                      onChange={(e) => props.onFormChange({ amount: e.target.value })}
                      placeholder={t('cv.amount_placeholder')}
                      className={`w-full bg-slate-50 dark:bg-[#05070a] border rounded-2xl pr-4 pl-12 py-3 font-mono text-lg font-bold transition-all ${
                        props.stockShort || props.cashShort
                          ? 'border-rose-500 ring-2 ring-rose-500/20 text-rose-600 dark:text-rose-400'
                          : 'border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500'
                      }`}
                    />
                    <button
                      type="button"
                      data-testid="cv-bills"
                      onClick={props.onToggleBillCounter}
                      className={`absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-xl border transition-all ${
                        props.showBillCounter
                          ? 'bg-indigo-500 border-indigo-600 text-white shadow-md'
                          : 'bg-white dark:bg-white/5 border-gray-200 dark:border-[#30363d] text-slate-400 hover:text-slate-600'
                      }`}
                      title={t('cv.bill_counter_title')}
                    >
                      <Calculator size={16} />
                    </button>
                  </div>

                  {props.stockShort && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid="cv-stock-short"
                      className="flex items-start gap-2.5 p-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs shadow-xs"
                    >
                      <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <p className="font-bold leading-snug">{t('cv.stock_short', { requested: props.stockShort.requestedLabel, available: props.stockShort.availableLabel, symbol: selected.symbol })}</p>
                        {props.stockShort.canFill && (
                          <button
                            type="button"
                            onClick={props.onFillMax}
                            className="inline-flex items-center gap-1 text-[11px] font-black text-rose-800 dark:text-rose-200 hover:underline cursor-pointer bg-rose-500/20 px-2 py-0.5 rounded-md transition-all active:scale-95"
                          >
                            {t('cv.fill_max', { available: props.stockShort.availableLabel, symbol: selected.symbol })}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )}

                  {props.cashShort && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      data-testid="cv-cash-short"
                      className="flex items-start gap-2.5 p-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 text-xs shadow-xs"
                    >
                      <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-bold leading-snug">{t('cv.cash_short', { cost: props.cashShort.costLabel, available: props.cashShort.availableLabel })}</p>
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Bill Denominations Counter Box (Collapsible) */}
                <AnimatePresence>
                  {props.showBillCounter && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden border border-gray-150 dark:border-[#30363d] rounded-2xl p-4 bg-slate-50/50 dark:bg-[#05070a]/50 space-y-3"
                    >
                      <span className="block text-[9px] font-black uppercase tracking-widest text-indigo-500">{t('cv.bill_counter')}</span>
                      <div className="grid grid-cols-2 gap-3">
                        {selected.bills.map((bill) => (
                          <div key={bill.value} className="flex items-center gap-2 bg-white dark:bg-[#0d1117] p-2 rounded-xl border border-gray-200 dark:border-[#30363d]">
                            <span className="w-12 text-xs font-mono font-bold text-slate-500">{bill.label}</span>
                            <input
                              type="text"
                              placeholder="0"
                              value={props.billCounts[bill.value] || ''}
                              onChange={(e) => props.onBillCountChange(bill.value, e.target.value)}
                              className="w-full text-center bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-lg py-1 font-mono font-bold text-xs"
                            />
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Rate & Cost entries for Buy/Sell; cost rate for an opening balance / direct deposit (D-068) */}
                {!isDirect && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{isSell ? t('cv.sell_rate_label', { unit, unitEn }) : t('cv.buy_rate_label', { unit, unitEn })}</label>
                        <input
                          type="text"
                          data-testid="cv-rate"
                          value={form.rate || ''}
                          onChange={(e) => props.onFormChange({ rate: e.target.value })}
                          placeholder={t('cv.rate_placeholder')}
                          className="w-full bg-slate-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-2xl px-4 py-3 font-mono font-bold text-sm text-center"
                        />
                      </div>

                      {isSell ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.cost_rate_label', { unit, unitEn })}</label>
                            <span className="bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400 text-[10px] px-1.5 py-0.5 rounded-md font-extrabold shadow-sm shrink-0">{t('cv.auto_avg')}</span>
                          </div>
                          <input
                            type="text"
                            data-testid="cv-cost"
                            value={form.costRate || ''}
                            onChange={(e) => props.onFormChange({ costRate: e.target.value })}
                            placeholder={t('cv.cost_placeholder')}
                            className="w-full bg-slate-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-2xl px-4 py-3 font-mono font-bold text-sm text-center"
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.equivalent_label', { unit, unitEn })}</label>
                          <div className="w-full bg-slate-100 dark:bg-white/5 border border-gray-150 dark:border-[#30363d] rounded-2xl px-4 py-3 font-mono font-black text-sm text-center text-slate-600 dark:text-slate-300" data-testid="cv-equivalent">
                            {props.preview?.equivalentLabel ?? '0'} {unit}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Live Profit / Loss Preview for Sell (server figures) */}
                    {isSell && props.preview?.profit && (
                      <div
                        data-testid="cv-profit-preview"
                        className={`p-3 rounded-2xl border transition-all text-xs ${
                          props.preview.profit.tone === 'positive'
                            ? 'bg-emerald-500/10 dark:bg-emerald-950/30 border-emerald-500/30 text-emerald-900 dark:text-emerald-300'
                            : props.preview.profit.tone === 'negative'
                              ? 'bg-rose-500/10 dark:bg-rose-950/30 border-rose-500/30 text-rose-900 dark:text-rose-300'
                              : 'bg-slate-100 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                        }`}
                      >
                        <div className="flex items-center justify-between font-bold">
                          <span className="flex items-center gap-1.5 font-black">
                            {props.preview.profit.tone === 'positive' ? (
                              <>
                                <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                <span>{t('cv.profit_positive')}</span>
                              </>
                            ) : props.preview.profit.tone === 'negative' ? (
                              <>
                                <TrendingDown className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
                                <span>{t('cv.profit_negative')}</span>
                              </>
                            ) : (
                              <span>{t('cv.profit_zero')}</span>
                            )}
                          </span>
                          <span
                            className={`font-mono font-black text-sm px-2.5 py-0.5 rounded-lg ${
                              props.preview.profit.tone === 'positive'
                                ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30'
                                : props.preview.profit.tone === 'negative'
                                  ? 'bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-500/30'
                                  : 'bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200'
                            }`}
                          >
                            {props.preview.profit.label} {unit}
                          </span>
                        </div>
                        <p className="text-[11px] mt-1.5 opacity-90 leading-relaxed font-medium">
                          {props.preview.profit.tone === 'positive'
                            ? t('cv.profit_positive_text', { diff: props.preview.profit.diffLabel, unit })
                            : props.preview.profit.tone === 'negative'
                              ? t('cv.profit_negative_text', { diff: props.preview.profit.diffLabel, loss: props.preview.profit.absLabel, unit })
                              : t('cv.profit_zero_text')}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {directNeedsCost && (
                  <div className="space-y-2">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{t('cv.cost_rate_label', { unit: t('cv.iqd'), unitEn: 'IQD' })}</label>
                    <input
                      type="text"
                      data-testid="cv-cost"
                      value={form.costRate || ''}
                      onChange={(e) => props.onFormChange({ costRate: e.target.value })}
                      placeholder={t('cv.cost_placeholder')}
                      className="w-full bg-slate-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-2xl px-4 py-3 font-mono font-bold text-sm text-center"
                    />
                  </div>
                )}

                {/* Note Entry (mandatory written reason for D-068 operations) */}
                <div className="space-y-2">
                  <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">{isDirect && form.directType !== 'opening' ? t('cv.reason_label') : t('cv.note_label')}</label>
                  <input
                    type="text"
                    data-testid="cv-note"
                    value={form.note || ''}
                    onChange={(e) => props.onFormChange({ note: e.target.value })}
                    placeholder={t('cv.note_placeholder')}
                    className="w-full bg-slate-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 rounded-2xl px-4 py-3 text-xs font-bold"
                  />
                </div>

                {/* Payment Due Date Alert - Only for Customer Account */}
                {!isDirect && form.counterparty === 'customer' && (
                  <div className="space-y-2.5 bg-amber-500/5 dark:bg-amber-500/10 p-3.5 rounded-2xl border border-amber-500/20 dark:border-amber-500/30 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-tight">
                        <Bell className="w-4 h-4 text-amber-500 animate-pulse" />
                        <span>{t('cv.due_title')}</span>
                      </label>
                      {form.dueDate ? (
                        <span className="text-[10px] font-black bg-amber-500/20 text-amber-800 dark:text-amber-300 px-2.5 py-0.5 rounded-lg border border-amber-500/30 font-mono">{t('cv.due_value', { date: form.dueDate })}</span>
                      ) : (
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500">{t('cv.optional')}</span>
                      )}
                    </div>

                    <div className="space-y-2">
                      <EnglishDatePicker value={form.dueDate || ''} onChange={(val) => props.onFormChange({ dueDate: val })} className="!py-2 !text-xs !bg-white dark:!bg-[#0d1117]" />

                      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 shrink-0">{t('cv.due_quick')}</span>
                        {[7, 15, 30].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => props.onDueDateShortcut(d)}
                            className="px-2 py-0.5 text-[10px] font-bold bg-white dark:bg-[#161b22] hover:bg-amber-50 dark:hover:bg-amber-950/40 text-amber-800 dark:text-amber-300 rounded-lg border border-amber-200/80 dark:border-amber-900/40 transition-all cursor-pointer shadow-xs active:scale-95"
                          >
                            +{d} {t(d === 7 ? 'cv.days_7' : 'cv.days_n')}
                          </button>
                        ))}
                        {form.dueDate && (
                          <button
                            type="button"
                            onClick={() => props.onFormChange({ dueDate: '' })}
                            className="px-2 py-0.5 text-[10px] font-bold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 rounded-lg border border-rose-200/80 dark:border-rose-900/40 hover:bg-rose-100 transition-all cursor-pointer shrink-0 mr-auto"
                          >
                            {t('cv.cancel')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  data-testid="cv-submit"
                  disabled={props.busy}
                  className={`w-full py-3.5 rounded-2xl font-black text-xs uppercase tracking-widest text-white shadow-md flex items-center justify-center gap-2 active:scale-[0.98] transition-all ${
                    submitTone === 'buy' ? 'bg-emerald-600 hover:bg-emerald-700' : submitTone === 'sell' ? 'bg-amber-600 hover:bg-amber-700' : submitTone === 'deposit' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                >
                  {submitTone === 'buy' ? <Plus size={16} /> : submitTone === 'sell' ? <TrendingUp size={16} /> : submitTone === 'deposit' ? <Plus size={16} /> : <Minus size={16} />}
                  {isBuy ? t('cv.submit_buy') : isSell ? t('cv.submit_sell') : t(`cv.submit_${form.directType}`)}
                </button>
              </form>
            )}
          </div>

          {/* B. Ledger & Movements Statement */}
          <div className="lg:col-span-2 bg-white dark:bg-[#0d1117] p-6 rounded-[2.5rem] border border-gray-150 dark:border-[#30363d] shadow-sm flex flex-col min-h-[450px]">
            <div className="space-y-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-slate-100 dark:bg-white/5 rounded-xl text-slate-600 dark:text-slate-300">
                    <ListFilter size={20} />
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 dark:text-white">{t('cv.ledger_title')}</h4>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('cv.ledger_sub')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={props.onPrint}
                    data-testid="cv-print"
                    className="p-3 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200 rounded-2xl transition-all flex items-center gap-2 text-xs font-bold shadow-sm"
                    title={t('cv.print_title')}
                  >
                    <Printer size={16} />
                    <span>{t('cv.print')}</span>
                  </button>
                </div>
              </div>

              {/* Filtering Controls */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 dark:bg-white/5 p-4 rounded-2xl border border-gray-100 dark:border-[#30363d]">
                <div className="col-span-2 sm:col-span-1 relative">
                  <Search size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    data-testid="cv-search"
                    value={props.search || ''}
                    onChange={(e) => props.onSearchChange(e.target.value)}
                    placeholder={t('cv.search')}
                    className="w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl pr-9 pl-3 py-2 text-xs font-bold"
                  />
                </div>
                <div>
                  <EnglishDatePicker value={props.startDate || ''} onChange={props.onStartDateChange} className="!py-2 !text-xs !bg-white dark:!bg-[#0d1117]" />
                </div>
                <div>
                  <EnglishDatePicker value={props.endDate || ''} onChange={props.onEndDateChange} className="!py-2 !text-xs !bg-white dark:!bg-[#0d1117]" />
                </div>
                <div>
                  <select
                    value={props.currencyFilter || ''}
                    data-testid="cv-currency-filter"
                    onChange={(e) => props.onCurrencyFilterChange(e.target.value)}
                    className="w-full bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2 text-xs font-bold"
                  >
                    <option value="all">{t('cv.all_currencies')}</option>
                    {props.currencyFilterOptions.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                {(['all', 'buy', 'sell'] as const).map((type) => (
                  <button
                    key={type}
                    data-testid={`cv-filter-${type}`}
                    onClick={() => props.onTypeFilterChange(type)}
                    className={`px-4 py-1.5 rounded-full font-bold transition-all ${
                      props.typeFilter === type
                        ? 'bg-slate-900 dark:bg-white text-white dark:text-black shadow-sm'
                        : 'bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {t(`cv.filter_${type}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto max-h-[350px] space-y-3 pr-2 scrollbar-thin" data-testid="cv-ledger">
              {props.rows.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center p-8 text-center text-slate-400 dark:text-gray-500 space-y-2 border-2 border-dashed border-gray-200 dark:border-white/5 rounded-3xl">
                  <AlertCircle size={32} className="opacity-40" />
                  <span className="text-sm font-black">{t('cv.empty_title')}</span>
                  <span className="text-xs">{t('cv.empty_text')}</span>
                </div>
              ) : (
                props.rows.map((r) => (
                  <div
                    key={r.id}
                    data-testid="cv-row"
                    className="p-4 bg-slate-50 dark:bg-white/5 rounded-2xl border border-gray-100 dark:border-[#30363d] flex items-center justify-between gap-4 text-xs group hover:border-indigo-500/20 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`px-3 py-1.5 rounded-xl text-[10px] font-black ${BADGE[r.kind]}`}>{t(`cv.row_${r.kind}`)}</div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-gray-800 dark:text-white">{r.currencyName}</span>
                          <span className="font-mono text-[10px] font-bold text-slate-400 px-1.5 py-0.5 bg-slate-100 dark:bg-white/5 rounded-md">{r.code}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock size={10} />
                            {r.dateTimeLabel}
                          </span>
                          <span className="flex items-center gap-1">
                            <User size={10} />
                            {r.operator}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right space-y-1.5">
                        <div className="font-sans font-black text-sm text-slate-900 dark:text-white flex items-center gap-1 justify-end">
                          <span>{r.kind === 'buy' || r.kind === 'deposit' || r.kind === 'opening' ? '+' : '-'}</span>
                          <span>{r.qtyLabel}</span>
                          <span className="text-[10px] text-slate-400">{r.symbol}</span>
                        </div>

                        {r.profit && (
                          <div className="flex items-center justify-end gap-1 font-mono text-[10px] font-black">
                            <span className="text-slate-400 font-bold">{t('cv.net_profit')}</span>
                            <span
                              className={`px-2 py-0.5 rounded-md ${
                                r.profit.positive
                                  ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/40'
                                  : 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-900/40'
                              }`}
                            >
                              {r.profit.label} د.ع
                            </span>
                          </div>
                        )}

                        {r.equivalentLabel && (
                          <div className="text-[10px] font-mono text-slate-400 flex items-center justify-end gap-2 flex-wrap">
                            {r.costLabel && (
                              <span className="text-[9px] text-slate-400 opacity-80">
                                ({t('cv.cost')} {r.costLabel})
                              </span>
                            )}
                            <span>
                              <span className="text-slate-400">{t('cv.row_equiv')} </span>
                              <span className="font-bold text-slate-600 dark:text-slate-300">{r.equivalentLabel} د.ع</span>
                            </span>
                          </div>
                        )}

                        {r.note && <p className="text-[10px] italic text-slate-400 dark:text-gray-400 max-w-[200px] truncate">{r.note}</p>}
                      </div>

                      {props.onDelete && (
                        <button
                          type="button"
                          onClick={() => props.onDelete!(r.id)}
                          className="p-2.5 rounded-xl bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/40 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 border border-rose-200/60 dark:border-rose-900/40 transition-all shadow-xs cursor-pointer active:scale-95 shrink-0 flex items-center justify-center"
                          title={t('cv.delete_row')}
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* 4. Currency Profit Analytics & Ranking Section - view_currency_profits */}
      {props.ranking && (
        <div className="bg-white dark:bg-[#0d1117] p-6 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm space-y-4" data-testid="cv-ranking">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl border border-emerald-500/20">
                <TrendingUp size={22} />
              </div>
              <div>
                <h3 className="text-sm font-black dark:text-white uppercase tracking-wider">{t('cv.ranking_title')}</h3>
                <p className="text-xs text-slate-400 dark:text-gray-400">{t('cv.ranking_sub')}</p>
              </div>
            </div>

            <div className="bg-emerald-50 dark:bg-emerald-950/30 px-4 py-2 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 text-emerald-700 dark:text-emerald-300 font-mono font-black text-sm">
              <span>{t('cv.ranking_total')}</span>
              <span>{header.profitTotalLabel} د.ع</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pt-2">
            {props.ranking.map((item, index) => {
              const rankBadge = index === 0 ? '🥇 #1' : index === 1 ? '🥈 #2' : index === 2 ? '🥉 #3' : `#${index + 1}`;
              return (
                <div
                  key={item.code}
                  className={`p-4 rounded-2xl border transition-all space-y-3 ${
                    item.isTop
                      ? 'bg-gradient-to-br from-amber-50/80 to-emerald-50/80 dark:from-amber-950/20 dark:to-emerald-950/20 border-amber-300 dark:border-amber-800/50 shadow-sm'
                      : 'bg-slate-50/60 dark:bg-[#161b22]/40 border-gray-200/80 dark:border-[#30363d]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-black text-xs px-2 py-0.5 rounded-lg bg-gray-200/60 dark:bg-gray-800 text-slate-700 dark:text-gray-300 font-mono">{rankBadge}</span>
                      <span className="font-black text-sm text-slate-900 dark:text-white">{item.name}</span>
                    </div>
                    <span className="text-xs font-mono font-bold text-slate-400">{item.code}</span>
                  </div>

                  <div className="space-y-1">
                    <div className="text-lg font-black font-sans text-emerald-600 dark:text-emerald-400">
                      {item.profitLabel} <span className="text-xs font-normal">د.ع</span>
                    </div>
                    <div className="flex justify-between items-center text-[10px] text-slate-400 font-medium">
                      <span>{t('cv.sales_count', { n: item.sellCount })}</span>
                      <span>
                        {item.pctShare}% {t('cv.of_profits')}
                      </span>
                    </div>
                  </div>

                  <div className="w-full bg-gray-200 dark:bg-gray-800 h-1.5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-500 ${item.isTop ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${item.pctShare}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
